// src/components/Dashboard/Dashboard.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { app, auth, API_BASE_URL }  from '../../firebase/firebaseConfig';
import { getAuth, signOut } from 'firebase/auth';
import { collection, collectionGroup, doc, getDoc, getDocs, getFirestore, limit, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where } from 'firebase/firestore';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DeviceAccountGroup, OrphanedDevicesGroup } from './DeviceAccountGroup';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import HotplugSettingsViewer from './HotplugSettingsViewer';
import OverrideSettingsViewer from './OverrideSettingsViewer';
import InstantLogViewer from './InstantLogViewer';
import SettingsViewer from './SettingsViewer';
import RemoteManagementViewer from './RemoteManagementViewer';
import HealthTab from './HealthTab';
import SessionsTab from './SessionsTab';
import StatusTab from './StatusTab';
import AdminSupportChat from '../Support/AdminSupportChat';
import MessageInbox from '../Support/MessageInbox';
import DocumentViewer from './DocumentViewer';
import LogViewer from './LogViewer';
import { useAllConversations } from '../Support/useAdminSupportMessages';
import {
  LogOut,
  Users,
  Monitor,
  Plus,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  Download,
  AlertTriangle,
  User,
  Home,
  Settings,
  Snowflake,
  ChevronDown,
  ChevronRight,
  Gamepad2,
  Upload,
  FileText,
  Activity,
  MessageCircle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Mail,
  Play,
  BarChart3,
  PackageCheck,
  Package,
  Plug,
} from 'lucide-react';

// Helper to format relative time (e.g. "3d ago", "12h ago")
function formatRelativeTime(timestampMs) {
  if (!timestampMs) return null;
  const now = Date.now();
  const diffMs = now - timestampMs;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

// WiFi signal strength bars icon (1-4 bars based on RSSI dBm)
function WifiSignalBars({ rssiDbm }) {
  let bars;
  let color;
  if (rssiDbm > -50) {
    bars = 4; color = '#22c55e';
  } else if (rssiDbm > -60) {
    bars = 3; color = '#22c55e';
  } else if (rssiDbm > -70) {
    bars = 2; color = '#f97316';
  } else {
    bars = 1; color = '#ef4444';
  }

  const barWidth = 2.5;
  const gap = 1.5;
  const heights = [4, 7, 10, 13];
  const totalWidth = 4 * barWidth + 3 * gap;

  return (
    <svg
      width={totalWidth}
      height={14}
      viewBox={`0 0 ${totalWidth} 14`}
      className="flex-shrink-0"
      title={`WiFi RSSI: ${Math.round(rssiDbm)} dBm`}
    >
      {heights.map((h, i) => (
        <rect
          key={i}
          x={i * (barWidth + gap)}
          y={14 - h}
          width={barWidth}
          height={h}
          rx={0.5}
          fill={i < bars ? color : '#d1d5db'}
        />
      ))}
    </svg>
  );
}

function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newNumFamilies, setNewNumFamilies] = useState(2);
  const [newAccountType, setNewAccountType] = useState('freeHearthOwningAccount');
  const [newBatch, setNewBatch] = useState('');
  const [newCountry, setNewCountry] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isRemoving, setIsRemoving] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [activeTab, setActiveTab] = useState('accounts');
  const [devices, setDevices] = useState([]);
  const [availableAppVersions, setAvailableAppVersions] = useState([]);
  const [availableOsVersions, setAvailableOsVersions] = useState([]);
  const [logModalVisible, setLogModalVisible] = useState(false);
  const [logContent, setLogContent] = useState('');
  const [logLoading, setLogLoading] = useState(false);
  const [currentLogName, setCurrentLogName] = useState('');
  const [currentLogFamilyID, setCurrentLogFamilyID] = useState(null);
  const [currentLogPath, setCurrentLogPath] = useState(null);
  const [logReloading, setLogReloading] = useState(false);
  const logTimestampBeforeReload = useRef(null);
  const [igloos, setIgloos] = useState([]);
  const [expandedIgloos, setExpandedIgloos] = useState({});
  const [showSingleOccupant, setShowSingleOccupant] = useState(false);
  const [metricsData, setMetricsData] = useState({});
  const [remoteUserMetrics, setRemoteUserMetrics] = useState({});
  const [metricsListeners, setMetricsListeners] = useState({});
  const [showInstantLogViewer, setShowInstantLogViewer] = useState(false);
  const [selectedInstantLogDevice, setSelectedInstantLogDevice] = useState(null);
  const [showSettingsViewer, setShowSettingsViewer] = useState(false);
  const [hotplugFamilies, setHotplugFamilies] = useState(new Set());
  const [showHotplugViewer, setShowHotplugViewer] = useState(false);
  const [selectedHotplugDevice, setSelectedHotplugDevice] = useState(null);
  const [overrideFamilies, setOverrideFamilies] = useState(new Set());
  const [showOverrideSettingsViewer, setShowOverrideSettingsViewer] = useState(false);
  const [selectedOverrideDevice, setSelectedOverrideDevice] = useState(null);
  const [showRemoteManagementViewer, setShowRemoteManagementViewer] = useState(false);
  const [selectedRemoteManagementDevice, setSelectedRemoteManagementDevice] = useState(null);
  const [importFileType, setImportFileType] = useState('csv');
  const [importAccountType, setImportAccountType] = useState('freeHearthOwningAccount');
  const [isImporting, setIsImporting] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const [deviceAccounts, setDeviceAccounts] = useState([]);
  const [expandedDeviceAccounts, setExpandedDeviceAccounts] = useState({});
  const [allFamilyNames, setAllFamilyNames] = useState({});
  const [showSupportChat, setShowSupportChat] = useState(false);
  const [selectedChatUser, setSelectedChatUser] = useState(null);
  const [showMessageInbox, setShowMessageInbox] = useState(false);
  const [signedInCounts, setSignedInCounts] = useState({});
  const [sortField, setSortField] = useState('email');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedBatch, setSelectedBatch] = useState('all');
  const [deviceRssi, setDeviceRssi] = useState({});
  const [firstSigninTimes, setFirstSigninTimes] = useState({});
  const [deviceSortMode, setDeviceSortMode] = useState('alphabetic');
  const [lastIglooTimes, setLastIglooTimes] = useState({});
  const [iglooTimesLoading, setIglooTimesLoading] = useState(false);
  const [showShipSubscribeModal, setShowShipSubscribeModal] = useState(false);
  const [shipSubscribeUserId, setShipSubscribeUserId] = useState(null);
  const [shipUpdateDate, setShipUpdateDate] = useState(true);
  const [docViewerTarget, setDocViewerTarget] = useState(null); // { type: 'account'|'family', id: string }

  // Refs for incremental RSSI listener management (avoid cascade from devices state)
  const rssiListenersRef = useRef({}); // { deviceId: unsubscribeFn }
  const allFamilyNamesRef = useRef({}); // cached family names for deviceManagement listener
  const deviceVisibleRef = useRef({}); // { deviceId: { ...visible field values } } for smart-diff
  const latestDeviceListRef = useRef([]); // always-fresh device data for periodic flush
  const statusDataRef = useRef({}); // { deviceId: { everyFamilyAndAccountVisible: {...}, accountVisible: {...}, adminVisible: {...} } }
  const statusListenersRef = useRef({}); // { deviceId: [unsubscribeFn, ...] }
  const rootDataRef = useRef({}); // { deviceId: { ...raw root doc fields } } — fallback for pre-migration devices

  const { conversations, fetchConversations, getUnreadCountForAccount, getConversationStatus } = useAllConversations();

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Convert Firestore Timestamp or epoch ms to milliseconds
  const toMs = (ts) => {
    if (!ts) return 0;
    if (ts.toMillis) return ts.toMillis();
    if (typeof ts === 'number') return ts;
    if (ts.seconds) return ts.seconds * 1000;
    return 0;
  };

  // Get account setup completion time from setupCompletedAt field
  const getAccountSetupTime = (account) => {
    if (account.setupCompletedAt) return toMs(account.setupCompletedAt);
    return null;
  };

  // Get most recent first-sign-in time across all families in an account
  const getAccountLatestSignin = (account) => {
    const families = account.families || [];
    let latest = null;
    families.forEach(fid => {
      const t = firstSigninTimes[fid];
      if (t && (latest === null || t > latest)) latest = t;
    });
    return latest;
  };

  const sortedUsers = [...users].sort((a, b) => {
    let aVal, bVal;
    switch (sortField) {
      case 'email': aVal = a.email || ''; bVal = b.email || ''; break;
      case 'id': aVal = a.id || ''; bVal = b.id || ''; break;
      case 'name': aVal = a.nameAtOrder || ''; bVal = b.nameAtOrder || ''; break;
      case 'batch': aVal = a.batch || ''; bVal = b.batch || ''; break;
      case 'country': aVal = a.shippingCountryAtOrder || ''; bVal = b.shippingCountryAtOrder || ''; break;
      case 'type': aVal = a.accountType || ''; bVal = b.accountType || ''; break;
      case 'families': aVal = a.numOfFamilies ?? 0; bVal = b.numOfFamilies ?? 0; break;
      case 'status': {
        // Primary: setupCompleted before accountCreated (when ascending)
        const aIsSetup = a.accountStatus === 'setupCompleted' ? 1 : 0;
        const bIsSetup = b.accountStatus === 'setupCompleted' ? 1 : 0;
        if (aIsSetup !== bIsSetup) {
          return sortDirection === 'asc' ? bIsSetup - aIsSetup : aIsSetup - bIsSetup;
        }
        // Secondary: within setupCompleted, sort by recency (most recent first)
        if (aIsSetup && bIsSetup) {
          const aTime = getAccountSetupTime(a) || 0;
          const bTime = getAccountSetupTime(b) || 0;
          if (aTime !== bTime) return sortDirection === 'asc' ? bTime - aTime : aTime - bTime;
        }
        // Fallback: alphabetical by email
        return (a.email || '').localeCompare(b.email || '');
      }
      default: aVal = ''; bVal = '';
    }
    if (sortField === 'status') return 0; // already handled above
    if (typeof aVal === 'number') {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }
    const cmp = String(aVal).localeCompare(String(bVal));
    return sortDirection === 'asc' ? cmp : -cmp;
  });

  // Batch list for filter dropdown
  const accountBatches = [...new Set(users.map(u => u.batch).filter(Boolean))].sort();

  // Filter sorted users by selected batch
  const displayedUsers = selectedBatch === 'all'
    ? sortedUsers
    : sortedUsers.filter(u => u.batch === selectedBatch);

  const SortHeader = ({ field, children, align = 'left' }) => (
    <th
      className={`text-${align} py-1.5 px-2 font-medium text-sm cursor-pointer hover:bg-muted/50 select-none`}
      onClick={() => handleSort(field)}
    >
      <div className={`flex items-center gap-1 ${align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : ''}`}>
        {children}
        {sortField === field ? (
          sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-30" />
        )}
      </div>
    </th>
  );

  const handleOpenChat = (user) => {
    setSelectedChatUser(user);
    setShowSupportChat(true);
  };

  const handleCloseChat = () => {
    setShowSupportChat(false);
    setSelectedChatUser(null);
    fetchConversations();
  };

  // Track which families have an active hotplug doc (written by device on sign-in, deleted on sign-out).
  // Used to enable/disable the per-device hotplug button.
  useEffect(() => {
    const db = getFirestore(app);
    const unsubscribe = onSnapshot(collectionGroup(db, 'hotplug'), (snapshot) => {
      const families = new Set();
      snapshot.docs.forEach(d => {
        // Path: deviceManagement/{familyID}/hotplug/{docId}
        const familyID = d.ref.parent.parent?.id;
        if (familyID) families.add(familyID);
      });
      setHotplugFamilies(families);
    }, (error) => {
      console.error('AdminDashboard: hotplug collectionGroup listener error:', error);
    });
    return () => unsubscribe();
  }, []);

  // Track which families have active override settings.
  // Used to visually indicate the per-device override button.
  useEffect(() => {
    const db = getFirestore(app);
    const unsubscribe = onSnapshot(collectionGroup(db, 'overrideSettings'), (snapshot) => {
      const families = new Set();
      snapshot.docs.forEach(d => {
        // Path: deviceManagement/{familyID}/overrideSettings/{category}
        const familyID = d.ref.parent.parent?.id;
        if (familyID && Object.keys(d.data()).length > 0) families.add(familyID);
      });
      setOverrideFamilies(families);
    }, (error) => {
      console.error('AdminDashboard: overrideSettings collectionGroup listener error:', error);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (activeTab !== 'devices') return;

    const db = getFirestore(app);

    // Fetch accounts for grouping devices, and fetch family names for all families
    const fetchAccounts = async () => {
      const querySnapshot = await getDocs(collection(db, 'accountsInfo'));
      const accountsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      accountsList.sort((a, b) => (a.email || '').localeCompare(b.email || ''));
      setDeviceAccounts(accountsList);

      // Start with all accounts collapsed (use Expand All button to open)
      setExpandedDeviceAccounts({});

      // Collect all unique family IDs across all accounts and fetch their names + sign-in times
      const allFamilyIds = new Set();
      accountsList.forEach(account => {
        (account.families || []).forEach(fid => allFamilyIds.add(fid));
      });
      const nameMap = {};
      const signinTimes = {};
      await Promise.all(
        Array.from(allFamilyIds).map(async (familyId) => {
          try {
            const familySnap = await getDoc(doc(db, 'familyInfo', familyId));
            if (familySnap.exists()) {
              const data = familySnap.data();
              nameMap[familyId] = data.name || familyId;
              if (data.firstSignin) signinTimes[familyId] = toMs(data.firstSignin);
            } else {
              nameMap[familyId] = familyId;
            }
          } catch (e) {
            nameMap[familyId] = familyId;
          }
        })
      );
      setAllFamilyNames(nameMap);
      allFamilyNamesRef.current = nameMap;
      setFirstSigninTimes(prev => ({ ...prev, ...signinTimes }));
    };
    fetchAccounts();

    // Smart-diff listener: only triggers React re-render when user-visible fields change.
    // Raw pings that don't change online/offline status are silently absorbed.
    const ONLINE_THRESHOLD_MS = 35 * 1000; // 30s ping period + 5s buffer

    const getVisibleSnapshot = (device) => {
      const status = statusDataRef.current[device.id] || {};
      const famAccVis = status.everyFamilyAndAccountVisible || {};
      const accVis = status.accountVisible || {};
      const adminVis = status.adminVisible || {};
      const root = rootDataRef.current[device.id] || {};

      const onlinePing = famAccVis.onlinePing ?? root.onlinePing;
      const pingMs = onlinePing?.toMillis?.() ?? 0;
      return {
        isOnline: pingMs > 0 && (Date.now() - pingMs < ONLINE_THRESHOLD_MS),
        currentAppVersion: accVis.currentAppVersion ?? root.currentAppVersion ?? '',
        targetAppVersion: accVis.targetAppVersion ?? root.targetAppVersion ?? '',
        instantUpdate: !!(accVis.instantUpdate ?? root.instantUpdate),
        otaStatus: adminVis.otaStatus ?? root.otaStatus ?? '',
        lastUploadedLogMs: (adminVis.lastUploadedLog ?? root.lastUploadedLog)?.toMillis?.() ?? 0,
        currentOsVersion: adminVis.currentOsVersion ?? root.currentOsVersion ?? '',
        targetOsVersion: adminVis.targetOsVersion ?? root.targetOsVersion ?? '',
        instantOsUpdate: !!(adminVis.instantOsUpdate ?? root.instantOsUpdate),
        osOtaStatus: adminVis.osOtaStatus ?? root.osOtaStatus ?? '',
      };
    };

    const unsubscribe = onSnapshot(collection(db, 'deviceManagement'), async (snapshot) => {
      const baseDeviceList = snapshot.docs.map(d => ({ id: d.id }));

      // Cache raw root data for fallback on pre-migration devices
      for (const d of snapshot.docs) {
        rootDataRef.current[d.id] = d.data();
      }

      // Use cached family names; only fetch for genuinely unknown devices
      const cachedNames = allFamilyNamesRef.current;
      const unknownIds = baseDeviceList
        .map(d => d.id)
        .filter(id => !(id in cachedNames));
      if (unknownIds.length > 0) {
        await Promise.all(
          unknownIds.map(async (id) => {
            try {
              const familySnap = await getDoc(doc(db, 'familyInfo', id));
              cachedNames[id] = familySnap.exists() ? familySnap.data().name : id;
            } catch (e) {
              cachedNames[id] = id;
            }
          })
        );
        allFamilyNamesRef.current = { ...cachedNames };
      }
      const deviceList = baseDeviceList.map(device => {
        const status = statusDataRef.current[device.id] || {};
        const famAccVis = status.everyFamilyAndAccountVisible || {};
        const accVis = status.accountVisible || {};
        const adminVis = status.adminVisible || {};
        const root = rootDataRef.current[device.id] || {};
        return {
          id: device.id,
          familyName: cachedNames[device.id] || device.id,
          onlinePing: famAccVis.onlinePing ?? root.onlinePing,
          currentAppVersion: accVis.currentAppVersion ?? root.currentAppVersion,
          targetAppVersion: accVis.targetAppVersion ?? root.targetAppVersion,
          instantUpdate: accVis.instantUpdate ?? root.instantUpdate,
          otaStatus: adminVis.otaStatus ?? root.otaStatus,
          lastUploadedLog: adminVis.lastUploadedLog ?? root.lastUploadedLog,
          logDumpStatus: adminVis.logDumpStatus,
          logDumpTimestamp: adminVis.logDumpTimestamp,
          logDumpPath: adminVis.logDumpPath,
          crashLogPath: adminVis.crashLogPath,
          currentOsVersion: adminVis.currentOsVersion ?? root.currentOsVersion,
          targetOsVersion: adminVis.targetOsVersion ?? root.targetOsVersion,
          instantOsUpdate: adminVis.instantOsUpdate ?? root.instantOsUpdate,
          osOtaStatus: adminVis.osOtaStatus ?? root.osOtaStatus,
        };
      });

      // Check if anything the user can see has actually changed
      const oldVisible = deviceVisibleRef.current;
      let changed = deviceList.length !== Object.keys(oldVisible).length;
      if (!changed) {
        for (const device of deviceList) {
          const newVis = getVisibleSnapshot(device);
          const oldVis = oldVisible[device.id];
          if (!oldVis) { changed = true; break; }
          for (const key of Object.keys(newVis)) {
            if (newVis[key] !== oldVis[key]) { changed = true; break; }
          }
          if (changed) break;
        }
      }

      // Always update the visible snapshot ref (cheap)
      const newVisible = {};
      for (const device of deviceList) {
        newVisible[device.id] = getVisibleSnapshot(device);
      }
      deviceVisibleRef.current = newVisible;

      // Always keep the ref fresh (cheap, no re-render)
      latestDeviceListRef.current = deviceList;

      // Only trigger a React re-render when something visible changed
      if (changed) {
        setDevices(deviceList);
      }
    });

    return () => unsubscribe();
  }, [activeTab]);

  // Listeners for status subcollection docs (everyFamilyAndAccountVisible, accountVisible, adminVisible)
  useEffect(() => {
    if (activeTab !== 'devices') {
      Object.values(statusListenersRef.current).forEach(unsubs => unsubs.forEach(u => u()));
      statusListenersRef.current = {};
      return;
    }
    if (devices.length === 0) return;

    const db = getFirestore(app);
    const currentIds = new Set(devices.map(d => d.id));
    const trackedIds = new Set(Object.keys(statusListenersRef.current));

    for (const id of trackedIds) {
      if (!currentIds.has(id)) {
        statusListenersRef.current[id].forEach(u => u());
        delete statusListenersRef.current[id];
        delete statusDataRef.current[id];
      }
    }

    for (const id of currentIds) {
      if (!trackedIds.has(id)) {
        const unsubs = [];
        const statusDocs = ['everyFamilyAndAccountVisible', 'accountVisible', 'adminVisible'];
        for (const statusDoc of statusDocs) {
          const ref = doc(db, 'deviceManagement', id, 'status', statusDoc);
          const unsub = onSnapshot(ref, (snap) => {
            if (!statusDataRef.current[id]) statusDataRef.current[id] = {};
            statusDataRef.current[id][statusDoc] = snap.exists() ? snap.data() : {};

            const latest = latestDeviceListRef.current;
            if (latest.length > 0) {
              const status = statusDataRef.current[id] || {};
              const famAccVis = status.everyFamilyAndAccountVisible || {};
              const accVis = status.accountVisible || {};
              const adminVis = status.adminVisible || {};
              const root = rootDataRef.current[id] || {};
              const updatedList = latest.map(device => {
                if (device.id !== id) return device;
                return {
                  ...device,
                  onlinePing: famAccVis.onlinePing,
                  currentAppVersion: accVis.currentAppVersion,
                  targetAppVersion: accVis.targetAppVersion ?? root.targetAppVersion,
                  instantUpdate: accVis.instantUpdate ?? root.instantUpdate,
                  otaStatus: adminVis.otaStatus,
                  lastUploadedLog: adminVis.lastUploadedLog,
                  logDumpStatus: adminVis.logDumpStatus,
                  logDumpTimestamp: adminVis.logDumpTimestamp,
                  logDumpPath: adminVis.logDumpPath,
                  crashLogPath: adminVis.crashLogPath,
                  currentOsVersion: adminVis.currentOsVersion,
                  targetOsVersion: adminVis.targetOsVersion ?? root.targetOsVersion,
                  instantOsUpdate: adminVis.instantOsUpdate ?? root.instantOsUpdate,
                  osOtaStatus: adminVis.osOtaStatus,
                };
              });
              latestDeviceListRef.current = updatedList;
              setDevices(updatedList);
            }
          });
          unsubs.push(unsub);
        }
        statusListenersRef.current[id] = unsubs;
      }
    }
  }, [devices, activeTab]);

  // Periodic flush: keeps devices state fresh so onlinePing doesn't go stale.
  // Without this, skipped setDevices() calls leave stale onlinePing in state,
  // causing devices to appear offline when other state changes trigger re-renders.
  useEffect(() => {
    if (activeTab !== 'devices') return;
    const interval = setInterval(() => {
      if (latestDeviceListRef.current.length > 0) {
        setDevices(latestDeviceListRef.current);
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [activeTab]);

  // Listen to each device's system log for WiFi RSSI data
  // Uses ref-based diffing to avoid tearing down all listeners when devices state changes
  useEffect(() => {
    if (activeTab !== 'devices') {
      Object.values(rssiListenersRef.current).forEach(unsub => unsub());
      rssiListenersRef.current = {};
      return;
    }
    if (devices.length === 0) return;
    const db = getFirestore(app);
    const currentIds = new Set(devices.map(d => d.id));
    const trackedIds = new Set(Object.keys(rssiListenersRef.current));
    for (const id of trackedIds) {
      if (!currentIds.has(id)) {
        rssiListenersRef.current[id]();
        delete rssiListenersRef.current[id];
      }
    }
    for (const id of currentIds) {
      if (!trackedIds.has(id)) {
        const systemLogRef = doc(db, 'deviceManagement', id, 'log', 'system');
        rssiListenersRef.current[id] = onSnapshot(systemLogRef, (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            const rssi = data["network.wifiRssiDbm"] ?? null;
            setDeviceRssi(prev => prev[id] === rssi ? prev : { ...prev, [id]: rssi });
          } else {
            setDeviceRssi(prev => prev[id] === null ? prev : { ...prev, [id]: null });
          }
        }, (error) => {
          console.error(`Error listening to system log for ${id}:`, error);
        });
      }
    }
  }, [activeTab, devices]);
  useEffect(() => {
    return () => {
      Object.values(rssiListenersRef.current).forEach(unsub => unsub());
      rssiListenersRef.current = {};
    };
  }, []);

  useEffect(() => {
    if (activeTab !== 'devices') return;

    const db = getFirestore(app);
    const q = query(
      collection(db, 'appUpdates'),
      where('status', '==', 'available'),
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.filter(doc => !isNaN(parseInt(doc.id, 10)));
      docs.sort((a, b) => {
        const timeA = a.data().timestamp?.toMillis?.() ?? 0;
        const timeB = b.data().timestamp?.toMillis?.() ?? 0;
        return timeB - timeA;
      });
      setAvailableAppVersions(docs);
    });

    return () => unsubscribe();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'devices') return;
    
    const db = getFirestore(app);
    const q = query(
      collection(db, 'osUpdates'),
      where('status', '==', 'available'),
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.filter(doc => !isNaN(parseInt(doc.id, 10)));
      docs.sort((a, b) => {
        const timeA = a.data().timestamp?.toMillis?.() ?? 0;
        const timeB = b.data().timestamp?.toMillis?.() ?? 0;
        return timeB - timeA;
      });
      setAvailableOsVersions(docs);
    });

    return () => unsubscribe();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'accounts') return;

    const db = getFirestore(app);

    const fetchAccounts = async () => {
      const querySnapshot = await getDocs(collection(db, 'accountsInfo'));
      const accountsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(accountsList);

      // Check which families have signed in and fetch first sign-in times
      const counts = {};
      const signinTimes = {};
      await Promise.all(
        accountsList.map(async (account) => {
          const families = account.families || [];
          if (families.length === 0) { counts[account.id] = 0; return; }
          let signedIn = 0;
          await Promise.all(
            families.map(async (familyId) => {
              try {
                const statusSnap = await getDoc(doc(db, 'deviceManagement', familyId, 'status', 'accountVisible'));
                if (statusSnap.exists() && statusSnap.data().currentAppVersion) {
                  signedIn++;
                }
                // Read firstSignin from familyInfo
                const familySnap = await getDoc(doc(db, 'familyInfo', familyId));
                if (familySnap.exists() && familySnap.data().firstSignin) {
                  signinTimes[familyId] = toMs(familySnap.data().firstSignin);
                }
              } catch (e) { /* ignore */ }
            })
          );
          counts[account.id] = signedIn;
        })
      );
      setSignedInCounts(counts);
      setFirstSigninTimes(signinTimes);
    };

    fetchAccounts();
  }, [activeTab]);

  // Check for openConversation query parameter (from email notification links)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const conversationUUID = urlParams.get('openConversation');

    if (conversationUUID && users.length > 0 && !showSupportChat) {
      // Find the user with this accountUUID
      const user = users.find(u => u.id === conversationUUID);
      if (user) {
        handleOpenChat(user);
        // Clean up the URL without refreshing the page
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [users, showSupportChat]);

  useEffect(() => {
    if (activeTab !== 'igloos') return;
    
    const db = getFirestore(app);
    const unsubscribe = onSnapshot(collection(db, 'iglooInfo'), async (snapshot) => {
      const baseIglooList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const iglooList = await Promise.all(
        baseIglooList.map(async (igloo) => {
          try {
            // Get the first controller (familyID) from the controllers array
            const controllerFamilyId = igloo.controllers?.[0];
            if (!controllerFamilyId) {
              return { ...igloo, iglooName: igloo.id, controllerName: 'No Controller' };
            }
            
            // Get the controller family name
            const familySnap = await getDoc(doc(db, 'familyInfo', controllerFamilyId));
            const controllerName = familySnap.exists() ? familySnap.data().name : controllerFamilyId;
            
            // Get the igloo name from the family's igloos collection
            const iglooSnap = await getDoc(doc(db, 'familyInfo', controllerFamilyId, 'igloos', igloo.id));
            const iglooName = iglooSnap.exists() ? iglooSnap.data().name : igloo.id;
            
            // Get member family names
            const memberFamilies = [];
            if (igloo.members && Array.isArray(igloo.members)) {
              for (const memberId of igloo.members) {
                try {
                  const memberSnap = await getDoc(doc(db, 'familyInfo', memberId));
                  const memberName = memberSnap.exists() ? memberSnap.data().name : memberId;
                  memberFamilies.push({ id: memberId, name: memberName });
                } catch (e) {
                  console.error(`Could not fetch member family info for ${memberId}`, e);
                  memberFamilies.push({ id: memberId, name: memberId });
                }
              }
            }
            
            // Combine controller and members into allFamilies for settings
            const allFamilies = [
              { id: controllerFamilyId, name: controllerName, isController: true },
              ...memberFamilies.map(member => ({ ...member, isController: false }))
            ];
            
            return { ...igloo, iglooName, controllerName, controllerFamilyId, memberFamilies, allFamilies };
          } catch (e) {
            console.error(`Could not fetch info for igloo ${igloo.id}`, e);
            return { ...igloo, iglooName: igloo.id, controllerName: 'Error loading', controllerFamilyId: null };
          }
        })
      );
      
      // Sort igloos alphabetically by igloo name, then by controller name
      const sortedIglooList = iglooList.sort((a, b) => {
        const iglooNameComparison = a.iglooName.localeCompare(b.iglooName);
        if (iglooNameComparison !== 0) {
          return iglooNameComparison;
        }
        return a.controllerName.localeCompare(b.controllerName);
      });
      
      setIgloos(sortedIglooList);
    });

    return () => unsubscribe();
  }, [activeTab]);

  // Cleanup metrics listeners on unmount
  useEffect(() => {
    return () => {
      // Cleanup all metrics listeners when component unmounts
      Object.keys(metricsListeners).forEach(iglooId => {
        const listeners = metricsListeners[iglooId];
        if (listeners) {
          Object.values(listeners).forEach(unsubscribe => unsubscribe());
        }
      });
    };
  }, [metricsListeners]);

  const handleTargetVersionChange = async (deviceId, newVersion) => {
    const db = getFirestore(app);
    const deviceRef = doc(db, 'deviceManagement', deviceId);
    const statusRef = doc(db, 'deviceManagement', deviceId, 'status', 'accountVisible');
    const value = parseInt(newVersion, 10);

    try {
      await Promise.all([
        setDoc(deviceRef, { targetAppVersion: value }, { merge: true }),
        setDoc(statusRef, { targetAppVersion: value }, { merge: true }),
      ]);
    } catch (error) {
      console.error(`Failed to update targetAppVersion for ${deviceId}:`, error);
    }
  };

  const handleInstantUpdateChange = async (deviceId, isChecked) => {
    const db = getFirestore(app);
    const deviceRef = doc(db, 'deviceManagement', deviceId);
    const statusRef = doc(db, 'deviceManagement', deviceId, 'status', 'accountVisible');

    try {
      await Promise.all([
        setDoc(deviceRef, { instantUpdate: isChecked }, { merge: true }),
        setDoc(statusRef, { instantUpdate: isChecked }, { merge: true }),
      ]);
    } catch (error) {
      console.error(`Failed to update instantUpdate for ${deviceId}:`, error);
    }
  };

  const handleTargetOsVersionChange = async (deviceId, newVersion) => {
    const db = getFirestore(app);
    const deviceRef = doc(db, 'deviceManagement', deviceId);
    const statusRef = doc(db, 'deviceManagement', deviceId, 'status', 'adminVisible');
    const value = parseInt(newVersion, 10);

    try {
      await Promise.all([
        setDoc(deviceRef, { targetOsVersion: value }, { merge: true }),
        setDoc(statusRef, { targetOsVersion: value }, { merge: true }),
      ]);
    } catch (error) {
      console.error(`Failed to update targetOsVersion for ${deviceId}:`, error);
    }
  };

  const handleInstantOsUpdateChange = async (deviceId, isChecked) => {
    const db = getFirestore(app);
    const deviceRef = doc(db, 'deviceManagement', deviceId);
    const statusRef = doc(db, 'deviceManagement', deviceId, 'status', 'adminVisible');

    try {
      await Promise.all([
        setDoc(deviceRef, { instantOsUpdate: isChecked }, { merge: true }),
        setDoc(statusRef, { instantOsUpdate: isChecked }, { merge: true }),
      ]);
    } catch (error) {
      console.error(`Failed to update instantOsUpdate for ${deviceId}:`, error);
    }
  };

  const handleRemoveUser = async (userEmail) => {
    setIsRemoving(true);
    try {
      const idToken = await auth.currentUser.getIdToken(true);

      const response = await fetch(`${API_BASE_URL}/admin/removeAccount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ email: userEmail }),
      });

      const data = await response.json();

      if (data.success) {
        setUsers(users.filter(user => user.email !== userEmail));
        setShowModal(false);
      } else {
        setErrorMessage(data.error || 'Failed to remove account');
      }
    } catch (error) {
      setErrorMessage('Error removing account: ' + error.message);
    } finally {
      setIsRemoving(false);
    }
  };

  const confirmRemoveUser = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const generateOrderCode = () => {
    const prefix = 'BETA';
    const randomNumber = Math.floor(Math.random() * 10000000000).toString().padStart(10, '0');
    return `${prefix}${randomNumber}`;
  };

  const handleAddUser = async () => {
    if (!/\S+@\S+\.\S+/.test(newUserEmail)) {
      setErrorMessage("Invalid email format");
      return;
    }

    if (newNumFamilies <= 0) {
      setErrorMessage("Number of families must be greater than 0");
      return;
    }

    setIsAddingUser(true);
    try {
      const idToken = await auth.currentUser.getIdToken(true);

      const accountData = {
        email: newUserEmail,
        numOfFamilies: parseInt(newNumFamilies, 10),
        accountType: newAccountType,
        orderCode: generateOrderCode(),
      };
      if (newUserName.trim()) accountData.nameAtOrder = newUserName.trim();
      if (newBatch.trim()) accountData.batch = newBatch.trim();
      if (newCountry) accountData.shippingCountryAtOrder = newCountry;

      const response = await fetch(`${API_BASE_URL}/admin/addAccount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify(accountData),
      });

      const data = await response.json();

      if (data.success) {
        // Refetch the accounts list to get the new account with its UUID
        const db = getFirestore(app);
        const querySnapshot = await getDocs(collection(db, 'accountsInfo'));
        const accountsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUsers(accountsList);
        setNewUserEmail('');
        setNewUserName('');
        setNewNumFamilies(2);
        setNewAccountType('freeHearthOwningAccount');
        setNewBatch('');
        setNewCountry('');
        setErrorMessage('');
      } else {
        setErrorMessage(data.error || 'Failed to add account');
      }
    } catch (error) {
      setErrorMessage('Error adding account: ' + error.message);
    } finally {
      setIsAddingUser(false);
    }
  };

  const handleImportAccounts = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsImporting(true);
    setImportMessage('');
    setErrorMessage('');

    try {
      const fileContent = await file.text();
      let accounts = [];

      // Parse file based on type
      if (importFileType === 'json') {
        accounts = JSON.parse(fileContent);
      } else if (importFileType === 'csv') {
        // Parse CSV
        const lines = fileContent.split('\n').filter(line => line.trim());

        // Helper function to parse a CSV line (handles quoted values)
        const parseCSVLine = (line) => {
          const values = [];
          let currentValue = '';
          let insideQuotes = false;

          for (let i = 0; i < line.length; i++) {
            const char = line[i];

            if (char === '"') {
              if (insideQuotes && line[i + 1] === '"') {
                currentValue += '"';
                i++;
              } else {
                insideQuotes = !insideQuotes;
              }
            } else if (char === ',' && !insideQuotes) {
              values.push(currentValue.trim());
              currentValue = '';
            } else {
              currentValue += char;
            }
          }
          values.push(currentValue.trim());
          return values;
        };

        const headers = parseCSVLine(lines[0]);

        accounts = lines.slice(1).map(line => {
          const values = parseCSVLine(line);

          const account = {};
          headers.forEach((header, index) => {
            let value = values[index] || '';

            // Skip type detection for empty values
            if (value === '') {
              account[header] = value;
              return;
            }

            // Try to parse JSON objects/arrays
            if ((value.startsWith('{') && value.endsWith('}')) ||
                (value.startsWith('[') && value.endsWith(']'))) {
              try {
                value = JSON.parse(value);
                account[header] = value;
                return;
              } catch (e) {
                // Keep as string if parsing fails
              }
            }

            // Detect booleans
            if (value === 'true') {
              account[header] = true;
              return;
            }
            if (value === 'false') {
              account[header] = false;
              return;
            }

            // Detect numbers (integer or float)
            if (/^-?\d+$/.test(value)) {
              account[header] = parseInt(value, 10);
              return;
            }
            if (/^-?\d+\.\d+$/.test(value)) {
              account[header] = parseFloat(value);
              return;
            }

            // Detect dates if field name contains "date" (case-insensitive)
            if (header.toLowerCase().includes('date')) {
              const parsedDate = new Date(value);
              if (!isNaN(parsedDate.getTime())) {
                account[header] = parsedDate;
                return;
              }
            }

            // Keep as string
            account[header] = value;
          });
          return account;
        });
      }

      // Import each account
      const idToken = await auth.currentUser.getIdToken(true);
      let successCount = 0;
      let failCount = 0;

      for (const account of accounts) {
        try {
          const response = await fetch(`${API_BASE_URL}/admin/importAccount`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              ...account,
              accountType: importAccountType,
            }),
          });

          const data = await response.json();
          if (data.success) {
            successCount++;
          } else {
            failCount++;
            console.error(`Failed to import ${account.email}:`, data.error);
          }
        } catch (error) {
          failCount++;
          console.error(`Error importing ${account.email}:`, error);
        }
      }

      // Refresh the accounts list
      const db = getFirestore(app);
      const querySnapshot = await getDocs(collection(db, 'accountsInfo'));
      const accountsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(accountsList);

      setImportMessage(`Import complete: ${successCount} successful, ${failCount} failed`);
    } catch (error) {
      setErrorMessage('Error importing accounts: ' + error.message);
    } finally {
      setIsImporting(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleShippingToggle = async (userId, newShippedStatus) => {
    if (newShippedStatus) {
      // Show confirmation modal asking about email subscription
      setShipSubscribeUserId(userId);
      setShowShipSubscribeModal(true);
      return;
    }
    // Toggling shipped off — no confirmation needed
    try {
      const db = getFirestore(app);
      const userRef = doc(db, 'accountsInfo', userId);
      await updateDoc(userRef, {
        shipped: newShippedStatus
      });
      const querySnapshot = await getDocs(collection(db, 'accountsInfo'));
      const accountsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(accountsList);
    } catch (error) {
      console.error('Error updating shipped status:', error);
    }
  };

  const handleShipWithSubscription = async (subscribe) => {
    const userId = shipSubscribeUserId;
    setShowShipSubscribeModal(false);
    setShipSubscribeUserId(null);
    setShipUpdateDate(true); // Reset for next time
    if (!userId) return;
    try {
      const db = getFirestore(app);
      const userRef = doc(db, 'accountsInfo', userId);
      const updateData = { shipped: true };
      if (shipUpdateDate) {
        updateData.shippedAt = serverTimestamp();
      }
      if (subscribe) {
        updateData.receiveEmailUpdates = true;
        // Only generate a new key if the account doesn't already have one,
        // to avoid breaking previously sent unsubscribe links
        const existingUser = users.find(u => u.id === userId);
        if (!existingUser?.emailUpdatesIdKey) {
          updateData.emailUpdatesIdKey = crypto.randomUUID();
        }
      }
      await updateDoc(userRef, updateData);
      const querySnapshot = await getDocs(collection(db, 'accountsInfo'));
      const accountsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(accountsList);
    } catch (error) {
      console.error('Error updating shipped status:', error);
    }
  };

  const handlePreparingForShippingToggle = async (userId, newPreparingStatus) => {
    try {
      const db = getFirestore(app);
      const userRef = doc(db, 'accountsInfo', userId);
      await updateDoc(userRef, {
        preparingForShipping: newPreparingStatus
      });
      // Refresh users list
      const querySnapshot = await getDocs(collection(db, 'accountsInfo'));
      const accountsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(accountsList);
    } catch (error) {
      console.error('Error updating preparing for shipping status:', error);
    }
  };

  const handleSignOut = async () => {
    const auth = getAuth(app);
    try {
      await signOut(auth).then(() => {
        window.location.reload();
      });
    } catch (err) {
      setErrorMessage('Failed to sign out');
    }
  };

  const toggleIgloo = (iglooId) => {
    const isExpanding = !expandedIgloos[iglooId];

    setExpandedIgloos(prev => ({
      ...prev,
      [iglooId]: isExpanding
    }));

    if (isExpanding) {
      // Create metrics listeners for this igloo
      startMetricsListeners(iglooId);
    } else {
      // Cleanup metrics listeners for this igloo
      stopMetricsListeners(iglooId);
    }
  };

  const toggleDeviceAccount = (accountId) => {
    setExpandedDeviceAccounts(prev => ({
      ...prev,
      [accountId]: !prev[accountId]
    }));
  };

  const expandAllDeviceAccounts = () => {
    const expanded = {};
    deviceAccounts.forEach(account => {
      expanded[account.id] = true;
    });
    setExpandedDeviceAccounts(expanded);
  };

  const collapseAllDeviceAccounts = () => {
    setExpandedDeviceAccounts({});
  };

  // O(1) device lookup map instead of repeated .find() over the full array
  const deviceMap = useMemo(() => {
    const map = new Map();
    devices.forEach(d => map.set(d.id, d));
    return map;
  }, [devices]);

  // Get devices that belong to a specific account (via families array)
  // Includes placeholder entries for families that don't have a deviceManagement doc yet
  const getDevicesForAccount = useCallback((account) => {
    const families = account.families || [];
    return families.map(familyId => {
      const realDevice = deviceMap.get(familyId);
      if (realDevice) {
        return realDevice;
      }
      // Placeholder for families without a deviceManagement doc
      return {
        id: familyId,
        familyName: allFamilyNames[familyId] || familyId,
        hasDeviceManagement: false
      };
    });
  }, [deviceMap, allFamilyNames]);

  // Get devices that don't belong to any account (orphaned devices)
  const orphanedDevices = useMemo(() => {
    const allAccountFamilies = new Set();
    deviceAccounts.forEach(account => {
      (account.families || []).forEach(familyId => {
        allAccountFamilies.add(familyId);
      });
    });
    return devices.filter(device => !allAccountFamilies.has(device.id));
  }, [deviceAccounts, devices]);

  // Fetch last igloo times for all families (called lazily when "Recent Igloo" sort is selected)
  const fetchIglooTimes = async () => {
    if (iglooTimesLoading) return;
    setIglooTimesLoading(true);
    const db = getFirestore(app);
    const allFamilyIds = new Set();
    deviceAccounts.forEach(account => {
      (account.families || []).forEach(fid => allFamilyIds.add(fid));
    });
    const times = {};
    try {
      // Fetch live sessions once
      const liveSnap = await getDocs(collection(db, 'iglooSessionsLive'));
      for (const sessionDoc of liveSnap.docs) {
        const participants = sessionDoc.data().participants || [];
        for (const participant of participants) {
          if (!allFamilyIds.has(participant.familyId)) continue;
          const intervals = participant.intervals || [];
          const lastInterval = intervals[intervals.length - 1];
          if (lastInterval?.joinedAt && !lastInterval?.leftAt) {
            times[participant.familyId] = Date.now();
          } else if (lastInterval?.leftAt) {
            const leftTime = lastInterval.leftAt.toMillis ? lastInterval.leftAt.toMillis() : lastInterval.leftAt;
            if (!times[participant.familyId] || leftTime > times[participant.familyId]) {
              times[participant.familyId] = leftTime;
            }
          }
        }
      }
    } catch (e) { /* ignore */ }
    // Fetch history per family
    await Promise.all(
      Array.from(allFamilyIds).map(async (familyId) => {
        try {
          const historyQuery = query(
            collection(db, 'iglooSessionsHistory'),
            where('participantFamilyIds', 'array-contains', familyId),
            limit(50)
          );
          const historySnap = await getDocs(historyQuery);
          for (const sessionDoc of historySnap.docs) {
            const endedAt = sessionDoc.data().endedAt;
            const endTime = endedAt?.toMillis ? endedAt.toMillis() : endedAt;
            if (endTime && (!times[familyId] || endTime > times[familyId])) {
              times[familyId] = endTime;
            }
          }
        } catch (e) { /* ignore */ }
      })
    );
    setLastIglooTimes(times);
    setIglooTimesLoading(false);
  };

  // Get most recent igloo time across all families in an account
  const getAccountLatestIgloo = (account) => {
    const families = account.families || [];
    let latest = null;
    families.forEach(fid => {
      const t = lastIglooTimes[fid];
      if (t && (latest === null || t > latest)) latest = t;
    });
    return latest;
  };

  // Sort device accounts based on selected sort mode (memoized)
  const sortedDeviceAccounts = useMemo(() => [...deviceAccounts].sort((a, b) => {
    switch (deviceSortMode) {
      case 'recentSetup': {
        const aTime = getAccountSetupTime(a) || 0;
        const bTime = getAccountSetupTime(b) || 0;
        if (aTime !== bTime) return bTime - aTime;
        return (a.email || '').localeCompare(b.email || '');
      }
      case 'recentSignin': {
        const aTime = getAccountLatestSignin(a) || 0;
        const bTime = getAccountLatestSignin(b) || 0;
        if (aTime !== bTime) return bTime - aTime;
        return (a.email || '').localeCompare(b.email || '');
      }
      case 'recentIgloo': {
        const aTime = getAccountLatestIgloo(a) || 0;
        const bTime = getAccountLatestIgloo(b) || 0;
        if (aTime !== bTime) return bTime - aTime;
        return (a.email || '').localeCompare(b.email || '');
      }
      default: // alphabetic
        return (a.email || '').localeCompare(b.email || '');
    }
  }), [deviceAccounts, deviceSortMode, firstSigninTimes, lastIglooTimes]);

  const startMetricsListeners = (iglooId) => {
    const db = getFirestore(app);
    const igloo = igloos.find(i => i.id === iglooId);
    
    if (!igloo?.allFamilies) return;

    const listeners = {};
    
    // Listen to individual family metrics
    igloo.allFamilies.forEach(family => {
      const metricsRef = doc(db, 'iglooInfo', iglooId, 'metrics', family.id);
      
      const unsubscribe = onSnapshot(metricsRef, (docSnap) => {
        if (docSnap.exists()) {
          setMetricsData(prev => ({
            ...prev,
            [iglooId]: {
              ...prev[iglooId],
              [family.id]: docSnap.data()
            }
          }));
        } else {
          // Set empty metrics if document doesn't exist
          setMetricsData(prev => ({
            ...prev,
            [iglooId]: {
              ...prev[iglooId],
              [family.id]: {
                sourceRms: 0,
                sourceNoise: 0,
                postGainRms: 0,
                postGainNoise: 0,
                postAgcRms: 0,
                postAgcNoise: 0
              }
            }
          }));
        }
      }, (error) => {
        console.error(`Failed to listen to metrics for family ${family.id} in igloo ${iglooId}:`, error);
      });

      listeners[family.id] = unsubscribe;
    });

    // Listen to remote user metrics for each family
    igloo.allFamilies.forEach(family => {
      const remoteUsersRef = collection(db, 'iglooInfo', iglooId, 'metrics', family.id, 'remoteUsers');
      
      const unsubscribeRemoteUsers = onSnapshot(remoteUsersRef, (querySnap) => {
        const remoteUsersData = {};
        querySnap.forEach((docSnap) => {
          remoteUsersData[docSnap.id] = docSnap.data();
        });
        
        setRemoteUserMetrics(prev => ({
          ...prev,
          [iglooId]: {
            ...prev[iglooId],
            [family.id]: remoteUsersData
          }
        }));
      }, (error) => {
        console.error(`Failed to listen to remote user metrics for family ${family.id} in igloo ${iglooId}:`, error);
      });

      listeners[`remoteUsers_${family.id}`] = unsubscribeRemoteUsers;
    });

    setMetricsListeners(prev => ({
      ...prev,
      [iglooId]: listeners
    }));
  };

  const stopMetricsListeners = (iglooId) => {
    const listeners = metricsListeners[iglooId];
    if (listeners) {
      // Unsubscribe all listeners for this igloo
      Object.values(listeners).forEach(unsubscribe => unsubscribe());
      
      // Remove listeners from state
      setMetricsListeners(prev => {
        const newListeners = { ...prev };
        delete newListeners[iglooId];
        return newListeners;
      });

      // Clear metrics data for this igloo
      setMetricsData(prev => {
        const newMetrics = { ...prev };
        delete newMetrics[iglooId];
        return newMetrics;
      });
    }
  };

  const handleViewLog = async (familyID, familyName, logPath) => {
    setLogLoading(true);
    setLogContent('Loading log...');
    setCurrentLogName(familyName);
    setCurrentLogFamilyID(familyID);
    setCurrentLogPath(logPath);
    setLogModalVisible(true);

    try {
      const idToken = await auth.currentUser.getIdToken(true);
      // Use logPath if available (new format), fall back to legacy format
      const body = logPath
        ? { logPath }
        : { logName: `${familyID}.log` };
      const response = await fetch(`${API_BASE_URL}/admin/fetchLog`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch log: ${response.status} ${response.statusText}`);
      }

      const logText = await response.text();
      setLogContent(logText);
    } catch (error) {
      console.error('Error downloading log:', error);
      setLogContent(`Error fetching log: ${error.message}`);
    } finally {
      setLogLoading(false);
    }
  };

  const handleViewCrashLog = async (familyID, familyName, crashLogPath) => {
    if (!crashLogPath) return;
    setLogLoading(true);
    setLogContent('Loading crash log...');
    setCurrentLogName(`${familyName} (crash)`);
    setCurrentLogFamilyID(familyID);
    setCurrentLogPath(crashLogPath);
    setLogModalVisible(true);

    try {
      const idToken = await auth.currentUser.getIdToken(true);
      const response = await fetch(`${API_BASE_URL}/admin/fetchLog`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ logPath: crashLogPath }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch crash log: ${response.status} ${response.statusText}`);
      }

      const logText = await response.text();
      setLogContent(logText);
    } catch (error) {
      console.error('Error downloading crash log:', error);
      setLogContent(`Error fetching crash log: ${error.message}`);
    } finally {
      setLogLoading(false);
    }
  };

  const handleRequestLogDump = async (familyID) => {
    try {
      const db = getFirestore();
      const dataRequestsRef = doc(db, 'deviceManagement', familyID, 'remoteManagement', 'dataRequests');
      await setDoc(dataRequestsRef, { logDump: true }, { merge: true });
    } catch (error) {
      console.error('Error requesting log dump:', error);
    }
  };

  // When reloading from LogViewer: snapshot the current timestamp, request dump, then wait for a newer one
  const handleLogViewerReload = async () => {
    if (!currentLogFamilyID) return;
    const device = devices.find(d => d.id === currentLogFamilyID);
    // Snapshot the current logDumpTimestamp so we can detect when it becomes newer
    const ts = device?.logDumpTimestamp;
    logTimestampBeforeReload.current = ts?.toMillis?.() ?? ts?.seconds * 1000 ?? 0;
    setLogReloading(true);
    await handleRequestLogDump(currentLogFamilyID);
  };

  // Watch for the device's logDumpTimestamp to change after a reload request
  useEffect(() => {
    if (!logReloading || !currentLogFamilyID || !logModalVisible) return;
    const device = devices.find(d => d.id === currentLogFamilyID);
    if (!device) return;

    // Error — stop waiting and tell the user
    if (device.logDumpStatus === 'error') {
      setLogReloading(false);
      return;
    }

    // Still uploading — keep waiting
    if (device.logDumpStatus === 'uploading') return;

    // Check if the timestamp is newer than what we had before
    const ts = device.logDumpTimestamp;
    const currentMs = ts?.toMillis?.() ?? ts?.seconds * 1000 ?? 0;
    if (currentMs <= logTimestampBeforeReload.current) return; // not yet updated

    // New log is ready — fetch it
    setLogReloading(false);
    setCurrentLogPath(device.logDumpPath);
    logTimestampBeforeReload.current = null;
    (async () => {
      setLogLoading(true);
      try {
        const idToken = await auth.currentUser.getIdToken(true);
        const response = await fetch(`${API_BASE_URL}/admin/fetchLog`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify({ logPath: device.logDumpPath }),
        });
        if (!response.ok) throw new Error(`Failed to fetch log: ${response.status}`);
        const logText = await response.text();
        setLogContent(logText);
      } catch (error) {
        console.error('Error fetching reloaded log:', error);
        setLogContent(`Error fetching log: ${error.message}`);
      } finally {
        setLogLoading(false);
      }
    })();
  }, [logReloading, currentLogFamilyID, logModalVisible, devices]);

  const handleViewInstantLog = (device) => {
    setSelectedInstantLogDevice(device);
    setShowInstantLogViewer(true);
  };

  const handleBackFromInstantLog = () => {
    setShowInstantLogViewer(false);
    setSelectedInstantLogDevice(null);
  };

  const handleViewSettings = () => {
    setShowSettingsViewer(true);
  };

  const handleBackFromSettings = () => {
    setShowSettingsViewer(false);
  };

  const handleViewHotplug = (device) => {
    setSelectedHotplugDevice(device);
    setShowHotplugViewer(true);
  };

  const handleBackFromHotplug = () => {
    setShowHotplugViewer(false);
    setSelectedHotplugDevice(null);
  };

  const handleViewOverrideSettings = (device) => {
    setSelectedOverrideDevice(device);
    setShowOverrideSettingsViewer(true);
  };

  const handleBackFromOverrideSettings = () => {
    setShowOverrideSettingsViewer(false);
    setSelectedOverrideDevice(null);
  };

  const handleViewRemoteManagement = (device) => {
    setSelectedRemoteManagementDevice(device);
    setShowRemoteManagementViewer(true);
  };

  const handleBackFromRemoteManagement = () => {
    setShowRemoteManagementViewer(false);
    setSelectedRemoteManagementDevice(null);
  };

  // Show InstantLogViewer if selected
  if (showInstantLogViewer && selectedInstantLogDevice) {
    return (
      <InstantLogViewer 
        initialDevice={selectedInstantLogDevice}
        onBack={handleBackFromInstantLog}
        allDevices={devices}
      />
    );
  }

  // Show SettingsViewer if selected
  if (showSettingsViewer) {
    return (
      <SettingsViewer
        onBack={handleBackFromSettings}
      />
    );
  }

  // Show HotplugSettingsViewer if selected
  if (showHotplugViewer && selectedHotplugDevice) {
    return (
      <HotplugSettingsViewer
        device={selectedHotplugDevice}
        onBack={handleBackFromHotplug}
      />
    );
  }

  // Show OverrideSettingsViewer if selected
  if (showOverrideSettingsViewer && selectedOverrideDevice) {
    return (
      <OverrideSettingsViewer
        device={selectedOverrideDevice}
        onBack={handleBackFromOverrideSettings}
      />
    );
  }

  // Show RemoteManagementViewer if selected
  if (showRemoteManagementViewer && selectedRemoteManagementDevice) {
    return (
      <RemoteManagementViewer 
        initialDevice={selectedRemoteManagementDevice}
        onBack={handleBackFromRemoteManagement}
        allDevices={devices}
      />
    );
  }

  return (
    <div className="mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-3">
          <Settings className="w-8 h-8" />
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowMessageInbox(true)}
            className="flex items-center gap-2 relative"
          >
            <MessageCircle className="w-4 h-4" />
            Messages
            {conversations.reduce((sum, c) => sum + (c.unreadByAdmin || 0), 0) > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center animate-pulse">
                {(() => {
                  const total = conversations.reduce((sum, c) => sum + (c.unreadByAdmin || 0), 0);
                  return total > 99 ? '99+' : total;
                })()}
              </span>
            )}
          </Button>
          <Button variant="outline" onClick={handleViewSettings} className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Global Settings
          </Button>
          <Button variant="destructive" onClick={handleSignOut} className="flex items-center gap-2">
            <LogOut className="w-4 h-4" />
            Sign Out
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="accounts" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Accounts
          </TabsTrigger>
          <TabsTrigger value="devices" className="flex items-center gap-2">
            <Monitor className="w-4 h-4" />
            Devices
          </TabsTrigger>
          <TabsTrigger value="igloos" className="flex items-center gap-2">
            <Snowflake className="w-4 h-4" />
            Igloos
          </TabsTrigger>
          <TabsTrigger value="health" className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Health
          </TabsTrigger>
          <TabsTrigger value="sessions" className="flex items-center gap-2">
            <Play className="w-4 h-4" />
            Sessions
          </TabsTrigger>
          <TabsTrigger value="status" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Status
          </TabsTrigger>
        </TabsList>

        {/* Accounts Tab */}
        <TabsContent value="accounts" className="space-y-6">
          {/* Accounts List */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Accounts List
                </CardTitle>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Batch:</span>
                  <Select value={selectedBatch} onValueChange={setSelectedBatch}>
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Batches ({users.length})</SelectItem>
                      {accountBatches.map(b => (
                        <SelectItem key={b} value={b}>
                          {b} ({users.filter(u => u.batch === b).length})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <SortHeader field="email">Email</SortHeader>
                      <SortHeader field="name">Name</SortHeader>
                      <SortHeader field="batch">Batch</SortHeader>
                      <SortHeader field="country">Country</SortHeader>
                      <SortHeader field="type" align="center">Type</SortHeader>
                      <SortHeader field="families" align="center">Families</SortHeader>
                      <SortHeader field="status" align="center">Status</SortHeader>
                      <th className="text-center py-1.5 px-2 font-medium text-sm">Preparing</th>
                      <th className="text-center py-1.5 px-2 font-medium text-sm">Shipped</th>
                      <th className="text-center py-1.5 px-2 font-medium text-sm">Delivered</th>
                      <th className="text-center py-1.5 px-2 font-medium text-sm">Messages</th>
                      <th className="text-right py-1.5 px-2 font-medium text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedUsers.map(user => (
                      <tr key={user.id} className="border-b hover:bg-muted/50 transition-colors">
                        <td className="py-1.5 px-2">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <div>
                              <span className="font-medium truncate block text-blue-600 hover:underline cursor-pointer" onClick={() => setDocViewerTarget({ type: 'account', id: user.id })}>{user.email}</span>
                              <span className="text-xs text-muted-foreground block hover:text-blue-600 hover:underline cursor-pointer" onClick={() => setDocViewerTarget({ type: 'account', id: user.id })}>{user.id}</span>
                            </div>
                          </div>
                        </td>
                        <td className="py-1.5 px-2 text-sm">
                          {user.nameAtOrder || '-'}
                        </td>
                        <td className="py-1.5 px-2 text-sm">
                          {user.batch || '-'}
                        </td>
                        <td className="py-1.5 px-2 text-sm">
                          {user.shippingCountryAtOrder || '-'}
                        </td>
                        <td className="py-1.5 px-2 text-center">
                          <Badge variant={user.accountType === 'invitedByCustomerAccount' ? 'secondary' : 'default'} className="text-xs">
                            {user.accountType === 'freeHearthOwningAccount' ? 'Free Hearth' :
                             user.accountType === 'payingHearthOwningAccount' ? 'Paying Hearth' :
                             user.accountType === 'invitedByCustomerAccount' ? 'Invited' : user.accountType}
                          </Badge>
                        </td>
                        <td className="py-1.5 px-2 text-center">
                          <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
                            <Home className="w-3 h-3" />
                            <span>{signedInCounts[user.id] ?? 0}/{user.numOfFamilies ?? 0}</span>
                          </div>
                        </td>
                        <td className="py-1.5 px-2 text-center">
                          <Badge variant={user.accountStatus === 'accountCreated' ? 'outline' : 'default'} className="text-xs">
                            {user.accountStatus}
                          </Badge>
                          {user.accountStatus === 'setupCompleted' && getAccountSetupTime(user) && (
                            <div className="text-[10px] text-muted-foreground mt-0.5">
                              {formatRelativeTime(getAccountSetupTime(user))}
                            </div>
                          )}
                        </td>
                        <td className="py-1.5 px-2 text-center">
                          <div className="flex justify-center">
                            <Switch
                              checked={user.preparingForShipping || false}
                              onCheckedChange={(toggledValue) => handlePreparingForShippingToggle(user.id, toggledValue)}
                            />
                          </div>
                        </td>
                        <td className="py-1.5 px-2 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <Switch
                              checked={user.shipped || false}
                              onCheckedChange={(toggledValue) => handleShippingToggle(user.id, toggledValue)}
                            />
                            {user.shipped && (
                              <Mail className={`w-3.5 h-3.5 ${user.receiveEmailUpdates ? 'text-blue-500' : 'text-muted-foreground/30'}`}
                                title={user.receiveEmailUpdates ? 'Subscribed to updates' : 'Not subscribed to updates'}
                              />
                            )}
                          </div>
                        </td>
                        <td className="py-1.5 px-2 text-center">
                          {user.shipped && user.trackingNumbers?.length > 0 && (() => {
                            const deliveryDates = user.deliveryDates || [];
                            const totalTracking = user.trackingNumbers.length;
                            const deliveredCount = user.trackingNumbers.filter((_, i) => deliveryDates[i] != null).length;
                            const isFullyDelivered = deliveredCount === totalTracking;
                            const isPartiallyDelivered = deliveredCount > 0 && deliveredCount < totalTracking;
                            const undeliveredTracking = user.trackingNumbers.filter((_, i) => deliveryDates[i] == null);

                            // Find the most recent delivery date for "days ago" display
                            const mostRecentDelivery = deliveryDates
                              .filter(d => d != null)
                              .map(d => d?.toDate ? d.toDate() : (d?.seconds ? new Date(d.seconds * 1000) : null))
                              .filter(Boolean)
                              .sort((a, b) => b - a)[0];

                            return (
                              <div className="flex flex-col items-center">
                                {isFullyDelivered ? (
                                  <>
                                    <div className="flex items-center gap-1 text-green-600">
                                      <PackageCheck className="w-3.5 h-3.5" />
                                      <span className="text-xs font-medium">Delivered</span>
                                    </div>
                                    {mostRecentDelivery && (
                                      <div className="text-[10px] text-muted-foreground mt-0.5">
                                        {formatRelativeTime(mostRecentDelivery.getTime())}
                                      </div>
                                    )}
                                  </>
                                ) : isPartiallyDelivered ? (
                                  <>
                                    <div className="flex items-center gap-1 text-amber-600">
                                      <Package className="w-3.5 h-3.5" />
                                      <span className="text-xs font-medium">Partial ({deliveredCount}/{totalTracking})</span>
                                    </div>
                                    <div className="text-[10px] text-muted-foreground mt-0.5">
                                      {undeliveredTracking.map((tn, i) => (
                                        <span key={tn}>
                                          {i > 0 && ', '}
                                          <a href={`https://www.royalmail.com/track-your-item#/tracking-results/${tn}`}
                                             target="_blank" rel="noopener noreferrer"
                                             className="text-blue-500 hover:underline">
                                            {tn}
                                          </a>
                                        </span>
                                      ))}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="flex items-center gap-1 text-muted-foreground">
                                      <Package className="w-3.5 h-3.5" />
                                      <span className="text-xs">Not Delivered</span>
                                    </div>
                                    <div className="text-[10px] text-muted-foreground mt-0.5">
                                      {user.trackingNumbers.map((tn, i) => (
                                        <span key={tn}>
                                          {i > 0 && ', '}
                                          <a href={`https://www.royalmail.com/track-your-item#/tracking-results/${tn}`}
                                             target="_blank" rel="noopener noreferrer"
                                             className="text-blue-500 hover:underline">
                                            {tn}
                                          </a>
                                        </span>
                                      ))}
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="py-1.5 px-2 text-center">
                          <div className="flex justify-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenChat(user)}
                              className="relative flex items-center gap-1"
                            >
                              <MessageCircle className="w-4 h-4" />
                              {getUnreadCountForAccount(user.id) > 0 && (
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-medium rounded-full h-5 w-5 flex items-center justify-center">
                                  {getUnreadCountForAccount(user.id) > 9 ? '9+' : getUnreadCountForAccount(user.id)}
                                </span>
                              )}
                              {getUnreadCountForAccount(user.id) === 0 &&
                               getConversationStatus(user.id) === 'open' && (
                                <span className="absolute -top-0.5 -right-0.5 bg-green-500 rounded-full h-2.5 w-2.5"></span>
                              )}
                            </Button>
                          </div>
                        </td>
                        <td className="py-1.5 px-2">
                          <div className="flex justify-end">
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => confirmRemoveUser(user)}
                              className="flex items-center gap-2"
                            >
                              <Trash2 className="w-3 h-3" />
                              Remove
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {users.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No accounts found.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Add Account Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Add New Account
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-w-2xl">
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <label className="text-sm font-medium mb-2 block">Email Address</label>
                    <Input
                      type="email"
                      value={newUserEmail}
                      onChange={(e) => setNewUserEmail(e.target.value)}
                      placeholder="Enter user email"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-sm font-medium mb-2 block">Name</label>
                    <Input
                      type="text"
                      value={newUserName}
                      onChange={(e) => setNewUserName(e.target.value)}
                      placeholder="Customer name"
                    />
                  </div>
                </div>
                <div className="flex gap-3 items-end">
                  <div className="w-32">
                    <label className="text-sm font-medium mb-2 block">Families</label>
                    <Input
                      type="number"
                      value={newNumFamilies}
                      onChange={(e) => setNewNumFamilies(e.target.value)}
                      placeholder="2"
                    />
                  </div>
                  <div className="w-56">
                    <label className="text-sm font-medium mb-2 block">Account Type</label>
                    <Select value={newAccountType} onValueChange={setNewAccountType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="freeHearthOwningAccount">Free</SelectItem>
                        <SelectItem value="payingHearthOwningAccount">Paid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-40">
                    <label className="text-sm font-medium mb-2 block">Batch</label>
                    <Input
                      type="text"
                      value={newBatch}
                      onChange={(e) => setNewBatch(e.target.value)}
                      placeholder="e.g. beta1"
                    />
                  </div>
                  <div className="w-40">
                    <label className="text-sm font-medium mb-2 block">Country</label>
                    <Select value={newCountry} onValueChange={setNewCountry}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GB">UK</SelectItem>
                        <SelectItem value="US">US</SelectItem>
                        <SelectItem value="AT">Austria</SelectItem>
                        <SelectItem value="BE">Belgium</SelectItem>
                        <SelectItem value="BG">Bulgaria</SelectItem>
                        <SelectItem value="HR">Croatia</SelectItem>
                        <SelectItem value="CY">Cyprus</SelectItem>
                        <SelectItem value="CZ">Czechia</SelectItem>
                        <SelectItem value="DK">Denmark</SelectItem>
                        <SelectItem value="EE">Estonia</SelectItem>
                        <SelectItem value="FI">Finland</SelectItem>
                        <SelectItem value="FR">France</SelectItem>
                        <SelectItem value="DE">Germany</SelectItem>
                        <SelectItem value="GR">Greece</SelectItem>
                        <SelectItem value="HU">Hungary</SelectItem>
                        <SelectItem value="IE">Ireland</SelectItem>
                        <SelectItem value="IT">Italy</SelectItem>
                        <SelectItem value="LV">Latvia</SelectItem>
                        <SelectItem value="LT">Lithuania</SelectItem>
                        <SelectItem value="LU">Luxembourg</SelectItem>
                        <SelectItem value="MT">Malta</SelectItem>
                        <SelectItem value="NL">Netherlands</SelectItem>
                        <SelectItem value="PL">Poland</SelectItem>
                        <SelectItem value="PT">Portugal</SelectItem>
                        <SelectItem value="RO">Romania</SelectItem>
                        <SelectItem value="SK">Slovakia</SelectItem>
                        <SelectItem value="SI">Slovenia</SelectItem>
                        <SelectItem value="ES">Spain</SelectItem>
                        <SelectItem value="SE">Sweden</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="default"
                    onClick={handleAddUser}
                    disabled={isAddingUser}
                    className="flex items-center gap-2 self-end"
                  >
                    <Plus className="w-4 h-4" />
                    {isAddingUser ? 'Adding...' : 'Add Account'}
                  </Button>
                </div>
              </div>

              {errorMessage && (
                <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2 text-destructive">
                  <AlertTriangle className="w-4 h-4" />
                  {errorMessage}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Import Accounts Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Import Accounts from File
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex gap-3 items-end max-w-2xl">
                  <div className="w-40">
                    <label className="text-sm font-medium mb-2 block">File Type</label>
                    <Select value={importFileType} onValueChange={setImportFileType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="csv">CSV</SelectItem>
                        <SelectItem value="json">JSON</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <label className="text-sm font-medium mb-2 block">Account Type</label>
                    <Select value={importAccountType} onValueChange={setImportAccountType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="freeHearthOwningAccount">Free Hearth Owning Account</SelectItem>
                        <SelectItem value="payingHearthOwningAccount">Paying Hearth Owning Account</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <label className="text-sm font-medium mb-2 block">Select File</label>
                    <div className="relative">
                      <input
                        type="file"
                        accept={importFileType === 'csv' ? '.csv' : '.json'}
                        onChange={handleImportAccounts}
                        disabled={isImporting}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-gray-900 file:text-white hover:file:bg-gray-800 disabled:opacity-50"
                      />
                    </div>
                  </div>
                </div>

                {isImporting && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-blue-700">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700"></div>
                    Importing accounts...
                  </div>
                )}

                {importMessage && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    {importMessage}
                  </div>
                )}

                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-sm font-medium mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    File Format Information:
                  </p>
                  <ul className="text-xs text-gray-600 space-y-1 ml-6 list-disc">
                    <li>CSV: First row must contain headers (email, numOfFamilies, nameAtOrder, shippingAddressAtOrder, shippingCountryAtOrder)</li>
                    <li>JSON objects like shippingAddressAtOrder should be formatted as valid JSON within the CSV cell</li>
                    <li>JSON: Array of account objects with the same fields</li>
                    <li>Each account will be assigned a UUID and accountStatus will be set to "accountCreated"</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Devices Tab */}
        <TabsContent value="devices" className="space-y-6">
          <Card>
            <CardHeader className="py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Monitor className="w-5 h-5" />
                  Device Management
                </CardTitle>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">Sort:</span>
                    <Select value={deviceSortMode} onValueChange={(value) => {
                      setDeviceSortMode(value);
                      if (value === 'recentIgloo' && Object.keys(lastIglooTimes).length === 0) fetchIglooTimes();
                    }}>
                      <SelectTrigger className="h-7 w-[150px] text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="alphabetic">Alphabetic</SelectItem>
                        <SelectItem value="recentSetup">Recent Setup</SelectItem>
                        <SelectItem value="recentSignin">Recent Signin</SelectItem>
                        <SelectItem value="recentIgloo">Recent Igloo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={expandAllDeviceAccounts}
                    disabled={Object.keys(expandedDeviceAccounts).filter(k => expandedDeviceAccounts[k]).length === deviceAccounts.length}
                    className="h-7 px-2 text-xs"
                  >
                    <ChevronDown className="w-3.5 h-3.5 mr-1" />
                    Expand All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={collapseAllDeviceAccounts}
                    disabled={Object.keys(expandedDeviceAccounts).filter(k => expandedDeviceAccounts[k]).length === 0}
                    className="h-7 px-2 text-xs"
                  >
                    <ChevronRight className="w-3.5 h-3.5 mr-1" />
                    Collapse All
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {deviceAccounts.length === 0 && devices.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Monitor className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No devices found.</p>
                  </div>
                ) : (
                  <>
                    {iglooTimesLoading && (
                      <div className="text-center py-2 text-xs text-muted-foreground">Loading igloo data...</div>
                    )}
                    {/* Render accounts with their devices */}
                    {sortedDeviceAccounts.map(account => (
                      <DeviceAccountGroup
                        key={account.id}
                        account={account}
                        accountDevices={getDevicesForAccount(account)}
                        isExpanded={!!expandedDeviceAccounts[account.id]}
                        onToggle={toggleDeviceAccount}
                        onViewAccountDocument={setDocViewerTarget}
                        sharedProps={{
                          availableAppVersions,
                          availableOsVersions,
                          onTargetVersionChange: handleTargetVersionChange,
                          onInstantUpdateChange: handleInstantUpdateChange,
                          onTargetOsVersionChange: handleTargetOsVersionChange,
                          onInstantOsUpdateChange: handleInstantOsUpdateChange,
                          onViewInstantLog: handleViewInstantLog,
                          onViewHotplug: handleViewHotplug,
                          onViewOverrideSettings: handleViewOverrideSettings,
                          onViewRemoteManagement: handleViewRemoteManagement,
                          onViewLog: handleViewLog,
                          onRequestLogDump: handleRequestLogDump,
                          onViewCrashLog: handleViewCrashLog,
                          onViewDocument: setDocViewerTarget,
                          hotplugFamilies,
                          overrideFamilies,
                          deviceRssi,
                          firstSigninTimes,
                        }}
                      />
                    ))}

                    {/* Orphaned Devices (not belonging to any account) */}
                    <OrphanedDevicesGroup
                      orphanedDevices={orphanedDevices}
                      sharedProps={{
                        availableAppVersions,
                        availableOsVersions,
                        onTargetVersionChange: handleTargetVersionChange,
                        onInstantUpdateChange: handleInstantUpdateChange,
                        onTargetOsVersionChange: handleTargetOsVersionChange,
                        onInstantOsUpdateChange: handleInstantOsUpdateChange,
                        onViewInstantLog: handleViewInstantLog,
                        onViewHotplug: handleViewHotplug,
                        onViewOverrideSettings: handleViewOverrideSettings,
                        onViewRemoteManagement: handleViewRemoteManagement,
                        onViewLog: handleViewLog,
                        onViewDocument: setDocViewerTarget,
                        hotplugFamilies,
                        overrideFamilies,
                        deviceRssi,
                        firstSigninTimes,
                      }}
                    />
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Igloos Tab */}
        <TabsContent value="igloos" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Snowflake className="w-5 h-5" />
                  Igloos Management
                </CardTitle>
                <div className="flex items-center gap-2">
                  <label htmlFor="show-single-occupant" className="text-sm font-medium">
                    Show single occupant
                  </label>
                  <Switch
                    id="show-single-occupant"
                    checked={showSingleOccupant}
                    onCheckedChange={setShowSingleOccupant}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {igloos
                  .filter(igloo => {
                    if (showSingleOccupant) return true;
                    return (igloo.allFamilies?.length || 0) > 1;
                  })
                  .map(igloo => (
                  <div key={igloo.id} className="border rounded-lg bg-card overflow-hidden">
                    {/* Igloo Header - Clickable to expand */}
                    <div 
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50"
                      onClick={() => toggleIgloo(igloo.id)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          {expandedIgloos[igloo.id] ? 
                            <ChevronDown className="w-4 h-4" /> : 
                            <ChevronRight className="w-4 h-4" />
                          }
                          <Snowflake className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                          <p className="font-medium">{igloo.iglooName}</p>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              Controller: {igloo.controllerName}
                            </span>
                            <span className="flex items-center gap-1">
                              <Settings className="w-3 h-3" />
                              ID: {igloo.id}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">
                          {(igloo.allFamilies?.length || 0)} member(s)
                        </Badge>
                      </div>
                    </div>

                    {/* Expandable Content */}
                    {expandedIgloos[igloo.id] && (
                      <div className="border-t bg-muted/30">
                        <div className="p-6">
                          <div className="space-y-6">
                            {igloo.allFamilies?.map(family => (
                              <div key={family.id} className="bg-background rounded-lg border">
                                {/* User Header */}
                                <div className="flex items-center gap-2 p-4 border-b bg-muted/50">
                                  <User className="w-5 h-5" />
                                  <h4 className="text-lg font-medium">{family.name}</h4>
                                  {family.isController && (
                                    <Badge variant="outline" className="text-xs">Controller</Badge>
                                  )}
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 divide-x">
                                  {/* Local Section */}
                                  <div className="p-4">
                                    <h5 className="text-md font-semibold mb-4 flex items-center gap-2">
                                      Local Metrics
                                    </h5>

                                    <div className="space-y-6">
                                      {/* Local Metrics */}
                                      <div>
                                        <div className="border rounded-md">
                                          <table className="w-full text-sm">
                                            <thead>
                                              <tr className="border-b bg-muted/50">
                                                <th className="text-left p-2 font-medium"></th>
                                                <th className="text-center p-2 font-medium">RMS (dBFS)</th>
                                                <th className="text-center p-2 font-medium">Noise (dBFS)</th>
                                                <th className="text-center p-2 font-medium">SNR (dB)</th>
                                                <th className="text-center p-2 font-medium">Gain (dB)</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              <tr className="border-b">
                                                <td className="p-2 font-medium">Source</td>
                                                <td className="p-2 text-center font-mono text-muted-foreground">
                                                  {metricsData[igloo.id]?.[family.id]?.sourceRms?.toFixed(1) ?? '0.0'}
                                                </td>
                                                <td className="p-2 text-center font-mono text-muted-foreground">
                                                  {metricsData[igloo.id]?.[family.id]?.sourceNoise?.toFixed(1) ?? '0.0'}
                                                </td>
                                                <td className="p-2 text-center font-mono text-muted-foreground">
                                                  {(() => {
                                                    const sourceRms = metricsData[igloo.id]?.[family.id]?.sourceRms ?? 0;
                                                    const sourceNoise = metricsData[igloo.id]?.[family.id]?.sourceNoise ?? 0;
                                                    return (sourceRms - sourceNoise).toFixed(1);
                                                  })()}
                                                </td>
                                                <td className="p-2 text-center"></td>
                                              </tr>
                                              <tr className="border-b">
                                                <td className="p-2 font-medium">Post Gain</td>
                                                <td className="p-2 text-center font-mono text-muted-foreground">
                                                  {metricsData[igloo.id]?.[family.id]?.postGainRms?.toFixed(1) ?? '0.0'}
                                                </td>
                                                <td className="p-2 text-center font-mono text-muted-foreground">
                                                  {metricsData[igloo.id]?.[family.id]?.postGainNoise?.toFixed(1) ?? '0.0'}
                                                </td>
                                                <td className="p-2 text-center font-mono text-muted-foreground">
                                                  {(() => {
                                                    const postGainRms = metricsData[igloo.id]?.[family.id]?.postGainRms ?? 0;
                                                    const postGainNoise = metricsData[igloo.id]?.[family.id]?.postGainNoise ?? 0;
                                                    return (postGainRms - postGainNoise).toFixed(1);
                                                  })()}
                                                </td>
                                                <td className="p-2 text-center"></td>
                                              </tr>
                                              <tr>
                                                <td className="p-2 font-medium">Post AGC</td>
                                                <td className="p-2 text-center font-mono text-muted-foreground">
                                                  {metricsData[igloo.id]?.[family.id]?.postAgcRms?.toFixed(1) ?? '0.0'}
                                                </td>
                                                <td className="p-2 text-center font-mono text-muted-foreground">
                                                  {metricsData[igloo.id]?.[family.id]?.postAgcNoise?.toFixed(1) ?? '0.0'}
                                                </td>
                                                <td className="p-2 text-center font-mono text-muted-foreground">
                                                  {(() => {
                                                    const postAgcRms = metricsData[igloo.id]?.[family.id]?.postAgcRms ?? 0;
                                                    const postAgcNoise = metricsData[igloo.id]?.[family.id]?.postAgcNoise ?? 0;
                                                    return (postAgcRms - postAgcNoise).toFixed(1);
                                                  })()}
                                                </td>
                                                <td className="p-2 text-center font-mono text-muted-foreground">
                                                  {(() => {
                                                    const postAgcRms = metricsData[igloo.id]?.[family.id]?.postAgcRms ?? 0;
                                                    const postGainRms = metricsData[igloo.id]?.[family.id]?.postGainRms ?? 0;
                                                    return (postAgcRms - postGainRms).toFixed(1);
                                                  })()}
                                                </td>
                                              </tr>
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Remote Section */}
                                  <div className="p-4">
                                    <h5 className="text-md font-semibold mb-4 flex items-center gap-2">
                                      Remote Metrics
                                    </h5>
                                    
                                    <div className="space-y-6">
                                      {/* Remote Metrics */}
                                      <div>
                                        <div className="space-y-4">
                                          {igloo.allFamilies?.filter(otherFamily => otherFamily.id !== family.id).map(otherFamily => (
                                            <div key={otherFamily.id} className="space-y-2">
                                              <h7 className="text-xs font-medium text-muted-foreground">{otherFamily.name}</h7>
                                              <div className="border rounded-md">
                                                <table className="w-full text-sm">
                                                  <thead>
                                                    <tr className="border-b bg-muted/50">
                                                      <th className="text-left p-2 font-medium"></th>
                                                      <th className="text-center p-2 font-medium">RMS (dBFS)</th>
                                                      <th className="text-center p-2 font-medium">Noise (dBFS)</th>
                                                      <th className="text-center p-2 font-medium">SNR (dB)</th>
                                                      <th className="text-center p-2 font-medium">Gain (dB)</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    <tr className="border-b">
                                                      <td className="p-2 font-medium">Received</td>
                                                      <td className="p-2 text-center font-mono text-muted-foreground">
                                                        {remoteUserMetrics[igloo.id]?.[family.id]?.[otherFamily.id]?.receivedRms?.toFixed(1) ?? '0.0'}
                                                      </td>
                                                      <td className="p-2 text-center font-mono text-muted-foreground">
                                                        {remoteUserMetrics[igloo.id]?.[family.id]?.[otherFamily.id]?.receivedNoise?.toFixed(1) ?? '0.0'}
                                                      </td>
                                                      <td className="p-2 text-center font-mono text-muted-foreground">
                                                        {(() => {
                                                          const receivedRms = remoteUserMetrics[igloo.id]?.[family.id]?.[otherFamily.id]?.receivedRms ?? 0;
                                                          const receivedNoise = remoteUserMetrics[igloo.id]?.[family.id]?.[otherFamily.id]?.receivedNoise ?? 0;
                                                          return (receivedRms - receivedNoise).toFixed(1);
                                                        })()}
                                                      </td>
                                                      <td className="p-2 text-center"></td>
                                                    </tr>
                                                    <tr>
                                                      <td className="p-2 font-medium">Post AGC</td>
                                                      <td className="p-2 text-center font-mono text-muted-foreground">
                                                        {remoteUserMetrics[igloo.id]?.[family.id]?.[otherFamily.id]?.postAgcRms?.toFixed(1) ?? '0.0'}
                                                      </td>
                                                      <td className="p-2 text-center font-mono text-muted-foreground">
                                                        {remoteUserMetrics[igloo.id]?.[family.id]?.[otherFamily.id]?.postAgcNoise?.toFixed(1) ?? '0.0'}
                                                      </td>
                                                      <td className="p-2 text-center font-mono text-muted-foreground">
                                                        {(() => {
                                                          const postAgcRms = remoteUserMetrics[igloo.id]?.[family.id]?.[otherFamily.id]?.postAgcRms ?? 0;
                                                          const postAgcNoise = remoteUserMetrics[igloo.id]?.[family.id]?.[otherFamily.id]?.postAgcNoise ?? 0;
                                                          return (postAgcRms - postAgcNoise).toFixed(1);
                                                        })()}
                                                      </td>
                                                      <td className="p-2 text-center font-mono text-muted-foreground">
                                                        {(() => {
                                                          const postAgcRms = remoteUserMetrics[igloo.id]?.[family.id]?.[otherFamily.id]?.postAgcRms ?? 0;
                                                          const receivedRms = remoteUserMetrics[igloo.id]?.[family.id]?.[otherFamily.id]?.receivedRms ?? 0;
                                                          return (postAgcRms - receivedRms).toFixed(1);
                                                        })()}
                                                      </td>
                                                    </tr>
                                                  </tbody>
                                                </table>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {igloos.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Snowflake className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No igloos found in iglooInfo collection.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Health Tab */}
        <TabsContent value="health" className="space-y-6">
          <HealthTab />
        </TabsContent>

        {/* Sessions Tab */}
        <TabsContent value="sessions" className="space-y-6">
          <SessionsTab />
        </TabsContent>

        {/* Status Tab */}
        <TabsContent value="status" className="space-y-6">
          <StatusTab />
        </TabsContent>
      </Tabs>

      {/* Confirmation Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-destructive" />
                Confirm Removal
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-6">Are you sure you want to remove <strong>{selectedUser?.email}</strong>?</p>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleRemoveUser(selectedUser.email)}
                  disabled={isRemoving}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  {isRemoving ? 'Removing...' : 'Remove'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Ship & Subscribe Modal */}
      {showShipSubscribeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Mark as Shipped
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shipUpdateDate}
                    onChange={(e) => setShipUpdateDate(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Update shipped date to now</span>
                </label>
              </div>
              <p className="mb-6">Also subscribe this customer to email updates?</p>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => { setShowShipSubscribeModal(false); setShipSubscribeUserId(null); setShipUpdateDate(true); }}>
                  Cancel
                </Button>
                <Button variant="outline" onClick={() => handleShipWithSubscription(false)}>
                  Ship only
                </Button>
                <Button onClick={() => handleShipWithSubscription(true)}>
                  Ship & subscribe
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Log Viewer */}
      {logModalVisible && (
        <LogViewer
          logContent={logContent}
          logName={currentLogName}
          loading={logLoading}
          reloading={logReloading}
          onClose={() => { setLogModalVisible(false); setLogReloading(false); }}
          onReload={handleLogViewerReload}
        />
      )}

      {/* Support Chat Modal */}
      {showSupportChat && selectedChatUser && (
        <AdminSupportChat
          accountUUID={selectedChatUser.id}
          userEmail={selectedChatUser.email}
          userName={selectedChatUser.name}
          onClose={handleCloseChat}
        />
      )}

      {/* Document Viewer Modal */}
      {docViewerTarget && (
        <DocumentViewer
          initialType={docViewerTarget.type}
          initialId={docViewerTarget.id}
          onClose={() => setDocViewerTarget(null)}
        />
      )}

      {/* Full-screen Message Inbox */}
      {showMessageInbox && (
        <MessageInbox
          conversations={conversations}
          users={users}
          onClose={() => setShowMessageInbox(false)}
        />
      )}
    </div>
  );
}

function parseOtaStatus(otaStatus) {
  if (otaStatus === undefined || otaStatus === null) return '';
  if (typeof otaStatus !== 'string') return String(otaStatus);

  try {
    const obj = JSON.parse(otaStatus);
    const status = obj.status;
    const version = obj.version;

    return (
      <>
        {status && <div className="text-xs">{status}</div>}
        {version && <div className="text-xs">{version}</div>}
      </>
    );
  } catch (e) {
    return otaStatus;
  }
}

export default AdminDashboard;
